/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import ItemList from 'flarum/common/utils/ItemList';

/**
 * A callback that runs once the visitor has successfully logged in or registered.
 *
 * Most callbacks return nothing; `get-on-success-route` returns the route that
 * should be navigated to instead of reloading the page.
 */
export type AuthSuccessCallback = () => string | void;

/**
 * The prioritised set of callbacks run after a successful log in / sign up.
 */
export type AuthSuccessCallbacks = ItemList<AuthSuccessCallback>;

/**
 * The receiver of the monkey-patched `onsubmit` below.
 */
export type LoadingModal = {
	loading: boolean;
};

declare module 'flarum/forum/components/LogInModal' {
	export default interface LogInModal<CustomAttrs extends ILoginModalAttrs = ILoginModalAttrs> {
		onLogInSuccessCallbacks(data: unknown): AuthSuccessCallbacks;
	}
}

declare module 'flarum/forum/components/SignUpModal' {
	export default interface SignUpModal<CustomAttrs extends ISignupModalAttrs = ISignupModalAttrs> {
		onSignUpSuccessCallbacks(data: unknown): AuthSuccessCallbacks;
	}
}

/**
 * HACK: Despite the fact that it is a bad idea to override flarum core code, it's the only sane way
 * to check if the user has logged in or registered successfully.
 * TODO: Please check for updates. Last check: v1.8.10
 * @see https://github.com/flarum/framework/blame/main/framework/core/js/src/forum/components/LogInModal.tsx
 * @see https://github.com/flarum/framework/blame/main/framework/core/js/src/forum/components/SignUpModal.tsx
 */
export default function loginExtender() {
	function callbacks(): AuthSuccessCallbacks {
		const callbacks: AuthSuccessCallbacks = new ItemList();
		callbacks.add('reload', () => reload(callbacks));

		// The following item is only added for extensibility purposes, it will be ignored by default.
		callbacks.add('get-on-success-route', m.route.get);

		return callbacks;
	}

	LogInModal.prototype.onLogInSuccessCallbacks = callbacks;

	override(LogInModal.prototype, 'onsubmit', function (this: LogInModal, _original: (e: SubmitEvent) => void, e: SubmitEvent) {
		e.preventDefault();

		(this as unknown as LoadingModal).loading = true;

		app.session.login(this.loginParams(), { errorHandler: this.onerror.bind(this) }).then((authorization) => {
			this.onLogInSuccessCallbacks(authorization)
				.toArray()
				.forEach((callback) => callback());
		}, this.loaded.bind(this));
	});

	SignUpModal.prototype.onSignUpSuccessCallbacks = callbacks;

	override(SignUpModal.prototype, 'onsubmit', function (this: SignUpModal, _original: (e: SubmitEvent) => void, e: SubmitEvent) {
		e.preventDefault();

		(this as unknown as LoadingModal).loading = true;

		const body = this.submitData();

		app
			.request({
				url: app.forum.attribute<string>('baseUrl') + '/register',
				method: 'POST',
				body,
				errorHandler: this.onerror.bind(this),
			})
			.then((userPayload) => {
				this.onSignUpSuccessCallbacks(userPayload)
					.toArray()
					.forEach((callback) => callback());
			}, this.loaded.bind(this));
	});
}

export const reload = (callbacks: AuthSuccessCallbacks) => {
	// `callbacks.get()` throws when the item was removed, so this may never be assigned below.
	let onSuccessRoute: string | void = undefined;
	try {
		onSuccessRoute = callbacks.get('get-on-success-route')();
	} catch (error) {
		//
	}

	if (onSuccessRoute) {
		window.location.replace(onSuccessRoute);
	} else {
		window.location.reload();
	}
};
